// src/studio/pages/DocumentStagePage.jsx
//
// One route component for every document stage — Requirements, Design,
// Technical Design are all this same page with a different `stage` prop.
// Which document type "design" actually means for this project comes from
// StudioProjectLayout's `applicableDocuments` (SPEC-07 D-2), read via
// useOutletContext rather than re-fetched here.
import PropTypes from "prop-types";
import { useOutletContext } from "react-router-dom";
import { Alert } from "../../shared/ui";
import DocumentWorkspace from "../components/DocumentWorkspace";

const DOCUMENT_LABELS = {
  requirement_document: "the Requirements Document",
  high_level_design: "the High Level Design",
  sfdc_solution_design: "the SFDC Solution Design",
  technical_design: "the Technical Design",
};

const STAGE_META = {
  requirements: {
    documentType: "requirement_document",
    title: "Requirements Document",
    description: "What the delivery team must build — scope, requirements, and constraints.",
  },
  design: {
    // Resolved dynamically below: SFDC Solution Design or the generic HLD.
    title: "Design",
    description: "How the solution is structured, ahead of technical build-out.",
  },
  technical: {
    documentType: "technical_design",
    title: "Technical Design",
    description: "Implementation-level detail the delivery team builds from directly.",
  },
};

export default function DocumentStagePage({ stage }) {
  const { project, designDocumentType, applicableDocuments } = useOutletContext();

  const meta = STAGE_META[stage];
  const documentType = stage === "design" ? designDocumentType : meta.documentType;
  const applicable = applicableDocuments.find((d) => d.document_type === documentType);

  if (applicable && !applicable.available) {
    return (
      <Alert variant="warning" title="This document isn't built yet">
        {meta.title} is specified but its section set hasn&rsquo;t shipped yet — check back once it
        has.
      </Alert>
    );
  }

  return (
    <DocumentWorkspace
      projectId={project.id}
      documentType={documentType}
      title={stage === "design" && documentType === "sfdc_solution_design" ? "SFDC Solution Design" : meta.title}
      description={meta.description}
      sourceDocumentType={applicable?.depends_on || undefined}
      sourceDocumentLabel={applicable?.depends_on ? DOCUMENT_LABELS[applicable.depends_on] : undefined}
    />
  );
}

DocumentStagePage.propTypes = {
  stage: PropTypes.oneOf(["requirements", "design", "technical"]).isRequired,
};
